import React, { useState, useMemo } from "react";
import { Plus, Search, Edit2, Trash2, Filter } from "lucide-react";
import type { Column, TableAction } from "@/components/ui/table";
import { CommonTable } from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import type { SkillDetail } from "@/interface/masterManagement";
import { useSkillList, useDeleteSkill } from "./hooks/useSkill";
import { useCategoryList } from "./hooks/useCategory";
import SkillFormModal from "./components/SkillFormModal";

const PAGE_SIZE = 10;

export const SkillManagement: React.FC = () => {
  const [search, setSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<SkillDetail | null>(null);

  const { data: categoryData } = useCategoryList({ page_size: 100 });
  const categories = categoryData?.data?.results || [];

  const { data: response, isLoading } = useSkillList({
    page: currentPage,
    page_size: PAGE_SIZE,
    search,
    category: +selectedCategoryId || undefined,
  });

  const deleteMutation = useDeleteSkill();

  const handleEdit = (row: SkillDetail) => {
    setSelectedSkill(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row: SkillDetail) => {
    setSelectedSkill(row);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSkill) return;
    try {
      await deleteMutation.mutateAsync(selectedSkill.id);
      setIsDeleteModalOpen(false);
      setSelectedSkill(null);
    } catch {
      // Error handled
    }
  };

  const getCategoryName = (row: SkillDetail) => {
    if (row.category_detail?.skill_category_name) {
      return row.category_detail.skill_category_name;
    }
    const catId = row.category;
    const cat = categories.find((c) => String(c.id) === String(catId));
    return cat?.skill_category_name || "Uncategorized";
  };

  const columns: Column<SkillDetail>[] = [
    {
      key: "skill_name",
      header: "Skill Name",
      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {row.skill_name}
        </span>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300">
          {getCategoryName(row)}
        </span>
      ),
    },
  ];

  const actions: TableAction<SkillDetail>[] = useMemo(
    () => [
      {
        label: "Edit",
        icon: () => <Edit2 className="w-4 h-4" />,
        onClick: handleEdit,
      },
      {
        label: "Delete",
        icon: () => <Trash2 className="w-4 h-4" />,
        variant: "danger",
        onClick: handleDeleteClick,
      },
    ],
    [],
  );

  const skillList = response?.data?.results || [];
  const totalPages = response?.data?.totalPages || 1;
  const totalItems = response?.data?.totalItems;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-6 bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm p-4 sm:p-6 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 max-w-xl">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search skills..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full h-10 pl-9 pr-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all placeholder:text-slate-400"
              />
            </div>

            {/* Category Filter Dropdown */}
            {/* <div className="relative min-w-[180px]">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <select
              value={selectedCategoryId}
              onChange={(e) => {
                setSelectedCategoryId(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full h-10 pl-9 pr-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all appearance-none text-slate-700 dark:text-slate-200"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.skill_category_name}
                </option>
              ))}
            </select>
          </div> */}
          </div>

          <Button
            onClick={() => {
              setSelectedSkill(null);
              setIsFormOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Skill</span>
          </Button>
        </div>

        <CommonTable
          columns={columns}
          data={skillList}
          actions={actions}
          loading={isLoading}
          emptyMessage="No skills found."
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            limit={PAGE_SIZE}
            onPageChange={(page) => setCurrentPage(page)}
            entryLabel="skills"
          />
        )}
      </div>
      <SkillFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedSkill(null);
        }}
        editData={selectedSkill}
      />

      <ConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedSkill(null);
        }}
        title="Delete Skill"
        description={`Are you sure you want to delete skill "${
          selectedSkill?.skill_name || ""
        }"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default SkillManagement;
